import { IdCard, FileText, CreditCard, MapPin, Clock, Phone, Navigation, MoonStar } from "lucide-react";
import { Reveal } from "./Reveal";
import { PHONE, TEL, LOT } from "./data";

const BRING = [
  { icon: IdCard, t: "Valid photo ID", d: "Driver's license or state-issued ID." },
  { icon: FileText, t: "Proof of ownership", d: "Registration or title in your name." },
  { icon: CreditCard, t: "Payment", d: "Cash, credit/debit cards or Zelle. No American Express." },
];

const INFO = [
  { id: "address", icon: MapPin, label: "Impound lot", lines: [LOT.street, LOT.city] },
  { id: "hours", icon: Clock, label: "Release hours", lines: ["Mon – Fri · 10 AM – 5 PM", "Sat – Sun · 10 AM – 2 PM"] },
  { id: "after-hours", icon: MoonStar, label: "After-hours pickup", lines: ["Available by phone", "$75 after-hours fee applies"] },
];

export const Retrieval = () => (
  <section id="retrieval" data-testid="retrieval-section" className="relative bg-[var(--bone)] text-[var(--ink)]">
    <div className="hazard h-3 w-full" />
    <div className="mx-auto grid max-w-[1400px] gap-14 px-5 py-24 sm:px-8 lg:grid-cols-12 lg:py-32">
      <Reveal className="lg:col-span-5">
        <p className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-zinc-600">
          <span className="text-[#4d7a1f]">05</span><span className="h-px w-10 bg-black/20" /> For vehicle owners
        </p>
        <h2 className="font-display text-6xl font-black uppercase leading-[0.85] sm:text-7xl lg:text-8xl">Got<br />towed?</h2>
        <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-700 sm:text-lg">
          Don't stress. Call us first to confirm we have your vehicle, then come by with the items below and we'll get you back on the road.
        </p>
        <a href={TEL} data-testid="retrieval-phone-cta" className="mt-8 inline-flex min-h-[52px] items-center gap-3 bg-[var(--ink)] px-6 font-display text-xl font-extrabold uppercase tracking-wide text-[var(--bone)] transition-transform duration-300 hover:-translate-y-1">
          <Phone className="h-5 w-5 text-[var(--signal)]" strokeWidth={2.5} /> {PHONE}
        </a>
      </Reveal>
      <div className="lg:col-span-7">
        <Reveal className="clip-notch relative mb-8 overflow-hidden bg-[var(--ink)]">
          <div data-testid="lot-map" className="relative aspect-[4/3] sm:aspect-[16/9]">
            <iframe title="Warrior impound lot map" src={LOT.mapEmbed} loading="lazy" referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0 h-full w-full border-0 grayscale-[0.4] contrast-[1.05]" />
          </div>
          <div className="flex flex-col gap-4 bg-[var(--ink)] p-5 text-[var(--bone)] sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">Impound lot</p>
              <p className="mt-1 font-display text-2xl font-extrabold uppercase leading-none sm:text-3xl">{LOT.street}</p>
              <p className="mt-1 text-sm text-zinc-300">{LOT.city}</p>
            </div>
            <a href={LOT.directions} target="_blank" rel="noopener noreferrer" data-testid="lot-directions-btn" className="group flex min-h-[52px] items-center justify-center gap-3 bg-[var(--signal)] px-6 font-display text-xl font-extrabold uppercase text-[var(--ink)] transition-transform duration-300 hover:-translate-y-1">
              <Navigation className="h-5 w-5 transition-transform duration-300 group-hover:rotate-12" strokeWidth={2.5} /> Get directions
            </a>
          </div>
        </Reveal>
        <div className="grid gap-px border border-black/15 bg-black/15 sm:grid-cols-3">
          {BRING.map((b, i) => (
            <Reveal key={b.t} delay={i * 0.08} y={20} className="bg-[var(--bone)] p-6">
              <div data-testid={`bring-item-${i + 1}`}>
                <b.icon className="h-6 w-6 text-[#4d7a1f]" strokeWidth={1.75} />
                <h3 className="mt-6 font-display text-2xl font-extrabold uppercase leading-none">{b.t}</h3>
                <p className="mt-2 text-sm text-zinc-700">{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mt-px grid gap-px border border-t-0 border-black/15 bg-black/15 sm:grid-cols-3">
          {INFO.map((x) => (
            <div key={x.id} data-testid={`retrieval-${x.id}`} className="flex items-start gap-4 bg-[var(--ink)] p-6 text-[var(--bone)]">
              <x.icon className="mt-0.5 h-5 w-5 shrink-0 text-[var(--signal)]" />
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-400">{x.label}</p>
                {x.lines.map((l) => <p key={l} className="mt-1 text-sm sm:text-base">{l}</p>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
